import { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Table, Alert, Button } from "reactstrap";
import {
  getTotalDispatchPending,
  getTotalOrderCompletedToday,
  getTotalOrderPlacedToday,
  getTotalPendingPicklist,
  getTotalPicklistInCheckerPanel,
  getWhatsAppLoginStatus,
} from "../../services/dashboard.service";
import { getCompany } from "../../services/company.service";
import useLocalStorage from "../../hooks/useLocalStorage";

function CommonCard({ title, count, value }: any) {
  return (
    <Card
      color='primary'
      className='text-white'
      style={{
        boxShadow: "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
      }}
    >
      <CardBody>
        <small className='fw-bolder'>{title}</small>
        <div className='d-flex justify-content-between fw-bolder'>
          <div>Count</div>
          <div>{count}</div>
        </div>
        <div className='d-flex justify-content-between fw-bolder'>
          {" "}
          <div>Value</div>
          <div>{value.toFixed(2)}</div>
        </div>
      </CardBody>
    </Card>
  );
}

export default function Home() {
  const [totalOrderPlacedToday, setTotalOrderPlacedToday] = useState({ count: 0, value: 0 });
  const [totalOrderCompletedToday, setTotalOrderCompletedToday] = useState({ count: 0, value: 0 });
  const [totalPendingPicklist, setTotalPendingPicklist] = useState({ count: 0, value: 0 });
  const [picklistInCheckerPanel, setPicklistInCheckerPanel] = useState({ count: 0, value: 0 });
  const [totalDispatchPending, setTotalDispatchPending] = useState([]);
  const [company, setCompany] = useLocalStorage<any>("company");
  const [whatsAppStatus, setWhatsAppStatus] = useState({
    isLoggedIn: false,
    mobileNumber: "",
    qrCodeUrl: "",
    isLoading: true,
    hasError: false,
    errorMessage: "",
  });

  const color = [
    "table-primary",
    "table-secondary",
    "table-success",
    "table-danger",
    "table-warning",
    "table-info",
  ];

  const fetchWhatsAppStatus = () => {
    setWhatsAppStatus((prev) => ({ ...prev, isLoading: true, hasError: false }));
    getWhatsAppLoginStatus()
      .then((response) => {
        console.log("WhatsApp Login Status:", response.data);
        if (response.data.status === "success") {
          // User is logged in
          setWhatsAppStatus({
            isLoggedIn: true,
            mobileNumber: response.data.data.connectedNumeber || "",
            qrCodeUrl: "",
            isLoading: false,
            hasError: false,
            errorMessage: "",
          });
        } else if (response.data.status === "qrcode") {
          // User needs to scan QR code
          setWhatsAppStatus({
            isLoggedIn: false,
            mobileNumber: "",
            qrCodeUrl: response.data.data,
            isLoading: false,
            hasError: false,
            errorMessage: "",
          });
        } else if (response.data.status === "error") {
          // API returned an error status
          setWhatsAppStatus({
            isLoggedIn: false,
            mobileNumber: "",
            qrCodeUrl: "",
            isLoading: false,
            hasError: true,
            errorMessage:
              response.data.message || "Unknown error occurred with WhatsApp connection",
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching WhatsApp login status:", error);
        setWhatsAppStatus({
          isLoggedIn: false,
          mobileNumber: "",
          qrCodeUrl: "",
          isLoading: false,
          hasError: true,
          errorMessage: error.response?.data?.message || "Failed to connect to WhatsApp service",
        });
      });
  };

  useEffect(() => {
    // Refresh company data to ensure we have the latest settings
    if (company && company.id) {
      getCompany(company.id)
        .then((response) => {
          const updatedCompany = response.data;
          setCompany(updatedCompany); // Update company data in localStorage
        })
        .catch((error) => {
          console.error("Failed to refresh company data:", error);
        });
    }

    getTotalOrderPlacedToday().then((response) => {
      setTotalOrderPlacedToday(response.data);
    });
    getTotalOrderCompletedToday().then((response) => {
      setTotalOrderCompletedToday(response.data);
    });
    getTotalPendingPicklist().then((response) => {
      setTotalPendingPicklist(response.data);
    });
    getTotalPicklistInCheckerPanel().then((response) => {
      setPicklistInCheckerPanel(response.data);
    });
    getTotalDispatchPending().then((response) => {
      setTotalDispatchPending(response.data);
    });

    // Initial fetch of WhatsApp status
    fetchWhatsAppStatus();
  }, []);

  return (
    <Row>
      <Col md={3}>
        <CommonCard
          title='Total order placed today'
          count={totalOrderPlacedToday.count}
          value={totalOrderPlacedToday.value}
        />
      </Col>
      <Col md={3}>
        <CommonCard
          title='Total order completed today'
          count={totalOrderCompletedToday.count}
          value={totalOrderCompletedToday.value}
        />
      </Col>
      <Col md={3}>
        <CommonCard
          title='Total pending picklist'
          count={totalPendingPicklist.count}
          value={totalPendingPicklist.value}
        />
      </Col>
      <Col md={3}>
        <CommonCard
          title='Total picklist pending for invoice'
          count={picklistInCheckerPanel.count}
          value={picklistInCheckerPanel.value}
        />
      </Col>

      <Col md={6} className='mt-3' color='primary'>
        <p className='fw-bolder'>Total Dispatch Pending Data</p>
        <Card>
          <Table className='mb-0 table-striped'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Count</th>
                <th>Box</th>
              </tr>
            </thead>
            <tbody>
              {totalDispatchPending.map((dispatchPending: any, index) => (
                <tr className={color[index]} key={index}>
                  <td>{dispatchPending.date}</td>
                  <td>{dispatchPending.count}</td>
                  <td>{dispatchPending.box}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </Col>
      {company.isEnableMessage && (
        <Col md={6} className='mt-3'>
          <p className='fw-bolder'>WhatsApp Status</p>
          <Card>
            <CardBody>
              <div className='d-flex justify-content-between align-items-center'>
                {!whatsAppStatus.isLoading && (
                  <Button size='sm' color='primary' onClick={fetchWhatsAppStatus}>
                    Refresh
                  </Button>
                )}
              </div>
              <hr />

              {whatsAppStatus.isLoading ? (
                <Alert color='info'>Loading WhatsApp status...</Alert>
              ) : whatsAppStatus.hasError ? (
                <Alert color='danger'>
                  <div className='d-flex flex-column'>
                    <p className='mb-2'>Error connecting to WhatsApp service:</p>
                    <p className='mb-2'>{whatsAppStatus.errorMessage}</p>
                    <Button
                      color='outline-danger'
                      size='sm'
                      className='mt-2 align-self-end'
                      onClick={fetchWhatsAppStatus}
                    >
                      Try Again
                    </Button>
                  </div>
                </Alert>
              ) : whatsAppStatus.isLoggedIn ? (
                <Alert color='success'>
                  <i className='bi bi-check-circle-fill me-2'></i>
                  Already logged in with {whatsAppStatus.mobileNumber}
                </Alert>
              ) : whatsAppStatus.qrCodeUrl ? (
                <div className='text-center'>
                  <p className='text-muted mt-2'>
                    <small>Scan this QR code to log in to WhatsApp</small>
                  </p>

                  <img
                    src={whatsAppStatus.qrCodeUrl}
                    alt='WhatsApp Login QR Code'
                    style={{ maxWidth: "200px", margin: "0 auto" }}
                  />
                </div>
              ) : (
                <Alert color='warning'>WhatsApp status unknown. Please try refreshing.</Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
}
